import React from 'react';
import PropTypes from 'prop-types';
import { styled, useTheme } from '@mui/material/styles';
import { Box, Grid, useMediaQuery } from '@mui/material';

import { CaretIcon, Link, Typography } from '@tkww/the-bash-frontend';

import { Image } from 'components/Image/Image';

const inlineStyle = {
  clickableGrid: {
    paddingTop: 6,
    paddingBottom: 6,
  },
};

const StyledLink = styled(Link)(({ theme }) => ({
  display: 'block',
  fontFamily: theme.fonts.bold,
}));

const VenueMarket = ({ market, index, marketImage }) => {
  const marketName = market.displayName.split(',')[0];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const width = isMobile ? 343 : 251;
  const height = isMobile ? 208 : 152;

  const displayVenueTypes = (m) =>
    m.crawlableVenueTypes.map((v) => (
      <Grid
        item
        xs={6}
        md={6}
        lg={4}
        key={v.fileName}
        sx={{
          padding: '6px 0',
          textAlign: { lg: 'left' },
          '&:first-child': {
            paddingTop: '0',
          },
        }}
        style={{ ...inlineStyle.clickableGrid }}
      >
        <StyledLink path={`/search/${v.fileName}-${m.fileName}`} nextPath="/search/[slug]">
          {v.displayName}
        </StyledLink>
      </Grid>
    ));

  return (
    <Grid
      container
      key={market.fileName}
      padding="40px 0"
      id={market.fileName}
      role="region"
      aria-label={`Venues in ${market.displayName}`}
      spacing={2}
    >
      <Grid item xs={12} md={6} lg={3}>
        <Image
          alt="venue image"
          src={`${marketImage}~sc_${width}.${height}`}
          lazyLoad={index > 1}
          quality={100}
          sx={{
            width: '100%',
            borderRadius: '3px',
            overflow: 'hidden',
            mb: '6px',
          }}
        />
        <Box sx={{ maxWidth: { md: 251 } }}>
          <Typography variant="h3" mb={1}>
            Venues in {market.displayName}
          </Typography>
          <Link
            type="standalone"
            path={`/search/venue-${market.fileName}`}
            nextPath="/search/[slug]"
            data-testid="seeAllLink"
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                paddingBottom: { xs: '20px', md: 0 },
                '& .icon': {
                  marginRight: 0.25,
                },
              }}
            >
              See All {marketName}{' '}
              <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 0.5 }}>
                Venues <CaretIcon direction="right" className="icon" />
              </Box>
            </Box>
          </Link>
        </Box>
      </Grid>
      <Grid item md={12} lg={9}>
        <Grid
          container
          data-testid="venueTypes"
          sx={{ paddingLeft: { lg: 4 }, overflow: { ld: 'hidden' } }}
        >
          {displayVenueTypes(market)}
        </Grid>
      </Grid>
    </Grid>
  );
};

VenueMarket.propTypes = {
  market: PropTypes.shape({
    id: PropTypes.number.isRequired,
    displayName: PropTypes.string.isRequired,
    fileName: PropTypes.string.isRequired,
    crawlableVenueTypes: PropTypes.arrayOf(
      PropTypes.shape({
        serviceId: PropTypes.number.isRequired,
        displayName: PropTypes.string.isRequired,
        fileName: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  marketImage: PropTypes.string.isRequired,
};

export default VenueMarket;
